import * as React from "react";
import { IconButton, styled } from "@mui/material";
import { SettingSwitch } from "./controls/settingSwitch";
import {
  ConfigOptionMetadata,
  KahunConfigurationOptionType,
} from "@kahunmedical/common-configuration";
import { SettingsTextInput } from "./controls/settingsTextInput";
import { SettingsNumericInput } from "./controls/settingsNumericInput";
import { SelectOptionsDropdown } from "./controls/selectOptionsDropdown";
import { ClinicThemeColorPicker } from "../themeCustomization/clinicThemeColorPicker";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { JsonSettingsEdit } from "./jsonSettingsEdit";
import { MessageTemplate } from "@kahunmedical/common-configuration/dist/configuration/messageTemplate";
import { MessageTemplateEditor } from "./controls/messageTemplateEditor";
import clsx from "clsx";
import { ImageIconEditor } from "./controls/imageIconEditor";
import { ClinicSelector } from "./controls/clinicSelector";
import { PolicySelector } from "./controls/policySelector";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { openInEditor } from "fe-common/utils/clinicUtils";
import { PanelSelector } from "./controls/panelSelector";
import { FeatureSelector } from "./controls/featureSelector";

//region [[ Styles ]]

const KahunSettingValueView = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  flex: 1,
  alignItems: "center",
  gap: 5,
  "&.array": {
    flexFlow: "column",
    alignItems: "center",
  },
  ".array &": {
    flexBasis: 0,
    flex: 1,
  },
});

const ArrayValueContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexFlow: "row",
  width: "100%",
});

const AddButton = styled(IconButton)({
  padding: 0,
  width: "48px",
});

const JsonSettingsEditor = styled(JsonSettingsEdit)({
  minWidth: "350px", // Make sure we can still access the edit drop down
});

const TextAndInputContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  width: "100%",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 5,
  "& span": {
    flex: 1,
    textAlign: "end",
    whiteSpace: "nowrap",
  },
  "& .settings-text-input": {
    width: "fit-content",
    flex: 1,
  },
});

const SmallIconButton = styled(IconButton)({
  "& svg": { width: 16, height: 16 },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface KahunSettingValueProps {
  className?: string;
  settingMetaData: ConfigOptionMetadata;
  onValueChange: (key: string, newValue: any) => void;
  value: any;
  source: string;
  ignoreArrays?: boolean;
  index?: number;
  // Sometimes we just want to manipulate it to being an array
  renderRecordAsArray?: boolean;
  disabled?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const getDefaultValueByDisplayType = (displayType) => {
  switch (displayType) {
    case KahunConfigurationOptionType.Boolean:
      return false;
    case KahunConfigurationOptionType.Color:
      return "#FFFFFF";
    case KahunConfigurationOptionType.Int:
      return 0;
    case KahunConfigurationOptionType.WPanel:
      return " ";
    case KahunConfigurationOptionType.JSON:
      return {};
    case KahunConfigurationOptionType.MessageTemplate: {
      return new MessageTemplate();
    }
    case KahunConfigurationOptionType.String:
    default:
      return " ";
  }
};

//endregion [[ Functions ]]
export const KahunSettingValue = React.memo(
  ({
    ignoreArrays,
    settingMetaData,
    source,
    disabled,
    ...props
  }: KahunSettingValueProps) => {
    // TODO: switch trough type and render components accordingly
    const handleValueChange = (newValue: any) => {
      console.log("settings value changed", settingMetaData.key, newValue);
      props.onValueChange(settingMetaData.key, newValue);
    };

    const renderArray = settingMetaData.isArray && !ignoreArrays;

    const disableAutoFocus = true;

    const removeArrayValue = (index: number) => {
      const retVal = props.value;

      return props.onValueChange(settingMetaData.key, [
        ...retVal.slice(0, index),
        ...retVal.slice(index + 1),
      ]);
    };

    const handleArrayValueChanged = (newValue: any, index: number) => {
      const retVal = props.value;
      return props.onValueChange(settingMetaData.key, [
        ...retVal.slice(0, index),
        newValue,
        ...retVal.slice(index + 1),
      ]);
    };

    const addArrayValue = () => {
      props.onValueChange(settingMetaData.key, [
        ...(props.value || []),
        getDefaultValueByDisplayType(settingMetaData.displayType),
      ]);
    };

    if (renderArray) {
      return (
        <KahunSettingValueView
          className={clsx("kahun-setting-value-view array", props.className)}
        >
          {props.value?.map?.((arrayValue, index) => (
            <ArrayValueContainer
              key={`${settingMetaData.key + index + arrayValue}`}
              className={"array-value-container"}
            >
              <KahunSettingValue
                index={index}
                settingMetaData={settingMetaData}
                source={source}
                onValueChange={(__, newVal) =>
                  handleArrayValueChanged(newVal, index)
                }
                value={props.value?.[index] === " " ? "" : props.value?.[index]}
                ignoreArrays /* Make sure we stop after one level */
                disabled={disabled}
              />
              <AddButton
                onClick={() => removeArrayValue(index)}
                disabled={disabled}
              >
                <RemoveCircleIcon />
              </AddButton>
            </ArrayValueContainer>
          ))}
          <AddButton onClick={addArrayValue} disabled={disabled}>
            <AddCircleIcon />
          </AddButton>
        </KahunSettingValueView>
      );
    }

    return (
      <KahunSettingValueView
        className={clsx("kahun-setting-value-view", props.className)}
      >
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.Boolean && (
          <SettingSwitch
            checked={props.value}
            onChange={(e, newValue) => handleValueChange(newValue)}
            disabled={disabled}
          />
        )}
        {(settingMetaData.displayType === KahunConfigurationOptionType.String ||
          settingMetaData.displayType ===
            KahunConfigurationOptionType.Text) && (
          <SettingsTextInput
            value={props.value}
            onChange={(e) => handleValueChange(e.target.value)}
            autoFocus={!disableAutoFocus}
            isTextArea={
              settingMetaData.displayType === KahunConfigurationOptionType.Text
            }
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType === KahunConfigurationOptionType.Color && (
          <ClinicThemeColorPicker
            colorKey={settingMetaData.displayName}
            color={props.value}
            onColorChange={(c) => handleValueChange(c.hex)}
            hideName
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType === KahunConfigurationOptionType.Int && (
          <SettingsNumericInput
            value={props.value}
            onChange={handleValueChange}
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType === KahunConfigurationOptionType.Float && (
          <SettingsNumericInput
            floatingPoint
            value={props.value}
            onChange={handleValueChange}
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.Choice && (
          <SelectOptionsDropdown
            options={settingMetaData.options}
            optionKey={settingMetaData.key}
            value={props.value}
            onChange={handleValueChange}
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.Clinic && (
          <ClinicSelector
            onSelect={handleValueChange}
            value={props.value}
            disabled={disabled}
            disableAutoFocus={disableAutoFocus}
          />
        )}
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.WPanel && (
          <TextAndInputContainer className={"text-item-container"}>
            {/*<SettingsTextInput*/}
            {/*  value={props.value}*/}
            {/*  onChange={(e) => handleValueChange(e.target.value)}*/}
            {/*  autoFocus={!disableAutoFocus}*/}
            {/*/>*/}
            <PanelSelector
              onSelect={handleValueChange}
              value={props.value}
              disabled={disabled}
              disableAutoFocus={disableAutoFocus}
            />
            {props.value && (
              <SmallIconButton
                onClick={() => openInEditor("featureEdit/" + props.value)}
                size="small"
                title="Go to panel settings"
              >
                <OpenInNewIcon />
              </SmallIconButton>
            )}
          </TextAndInputContainer>
        )}
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.WFeature && (
          <TextAndInputContainer className={"text-item-container"}>
            <FeatureSelector
              key={"featureSelector" + props.index}
              onSelect={handleValueChange}
              value={props.value}
              disabled={disabled}
              disableAutoFocus={disableAutoFocus}
              searchOptions={settingMetaData.customParams?.searchOptions}
            />
            {props.value && (
              <SmallIconButton
                onClick={() => openInEditor("featureEdit/" + props.value)}
                size="small"
                title="Go to feature page"
              >
                <OpenInNewIcon />
              </SmallIconButton>
            )}
          </TextAndInputContainer>
        )}
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.WPolicy && (
          <TextAndInputContainer className={"text-item-container"}>
            <PolicySelector
              onSelect={handleValueChange}
              value={props.value}
              disabled={disabled}
              disableAutoFocus={disableAutoFocus}
            />
            {props.value && (
              <SmallIconButton
                onClick={() => openInEditor("protocols/" + props.value)}
                size="small"
                title="Goto policy settings"
              >
                <OpenInNewIcon />
              </SmallIconButton>
            )}
          </TextAndInputContainer>
        )}
        {settingMetaData.displayType === KahunConfigurationOptionType.JSON && (
          <JsonSettingsEditor
            value={props.value}
            keyName={
              props.index || props.index === 0
                ? props.index.toString()
                : settingMetaData.key
            }
            onResetDefaultClicked={() => handleValueChange(null)}
            onSubmit={handleValueChange}
            disableWarning
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType ===
          KahunConfigurationOptionType.MessageTemplate && (
          <MessageTemplateEditor
            value={props.value}
            onChange={handleValueChange}
            source={source}
            disabled={disabled}
          />
        )}
        {settingMetaData.displayType === "image" && (
          <ImageIconEditor
            value={props.value}
            onChange={handleValueChange}
            disabled={disabled}
          />
        )}
      </KahunSettingValueView>
    );
  },
);
